import { useState, useEffect, useCallback } from "react";
import { Image, Compass, HelpCircle, Phone } from "lucide-react";
import { useTranslation } from "react-i18next";

const SECTION_IDS = ["layouts", "explore", "faq", "contact"];

const useActiveSection = () => {
  const [active, setActive] = useState("layouts");

  const handleScroll = useCallback(() => {
    const scrollY = window.scrollY;
    const windowH = window.innerHeight;

    // At the very bottom → contact
    if (scrollY + windowH >= document.body.scrollHeight - 60) {
      setActive("contact");
      return;
    }

    // Find section whose top is closest above viewport center
    const center = scrollY + windowH * 0.4;
    let best = "layouts";
    for (const id of SECTION_IDS) {
      const el = document.getElementById(id);
      if (el && el.offsetTop <= center) best = id;
    }
    setActive(best);
  }, []);

  useEffect(() => {
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  return active;
};

const BottomNav = () => {
  const { t } = useTranslation();
  const activeSection = useActiveSection();

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    const element = document.getElementById(href.slice(1));
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
      window.history.pushState(null, "", href);
    }
  };

  const navItems = [
    { icon: Image, label: t("nav.villasPlots"), href: "#layouts", section: "layouts" },
    { icon: Compass, label: t("nav.exploreHuaHin"), href: "#explore", section: "explore" },
    { icon: HelpCircle, label: t("nav.qa"), href: "#faq", section: "faq" },
    { icon: Phone, label: t("nav.contact"), href: "#contact", section: "contact" },
  ];

  return (
    <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-md border-t border-border/40 safe-area-bottom">
      <div className="flex items-center justify-around h-16 px-1">
        {navItems.map((item) => {
          const isActive = activeSection === item.section;
          return (
            <a
              key={item.href}
              href={item.href}
              onClick={(e) => handleNavClick(e, item.href)}
              aria-label={item.label}
              className={`relative flex flex-col items-center justify-center gap-0.5 min-w-[48px] flex-1 min-h-[48px] transition-opacity duration-300 ${
                isActive ? "text-accent" : "text-muted-foreground active:text-accent"
              }`}
            >
              <item.icon className="w-4 h-4" strokeWidth={isActive ? 2 : 1.5} />
              <span className="text-[9px] tracking-wide font-body truncate max-w-full px-0.5">{item.label}</span>
              <span
                className={`absolute -bottom-0.5 w-1 h-1 rounded-full bg-accent transition-all duration-300 ${
                  isActive ? "opacity-100 scale-100" : "opacity-0 scale-0"
                }`}
              />
            </a>
          );
        })}
      </div>
    </div>
  );
};

export default BottomNav;
